import { Component, Input, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { ActionFailed, Destroyable, isSomething, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { PhoneNumberService } from '../phone-number.service'
import { distinctUntilChanged, finalize, map } from 'rxjs/operators'
import * as lodash from 'lodash'
import { ErrorStateMatcher } from '@angular/material/core'
import { Store } from '@ngrx/store'

class PhoneNumberErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(isSomething(control?.errors) && (control.dirty || control.touched))
  }
}

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
})
export class PhoneNumberInputComponent extends Destroyable implements OnInit {
  _ctrl: FormControl
  matcher = new PhoneNumberErrorStateMatcher()

  @Input() set ctrl(c: FormControl) {
    this._ctrl = c
  }

  get ctrl() {
    return this._ctrl
  }

  @Input() prefixIcon = 'cs-icon cs-icon-phone'
  @Input() showPrefix = false
  @Input() showValidateBtn = true
  @Input() showLabel = true
  @Input() hint = ''

  @Input() label: string
  @Input() placeholder = $localize`Phone Number`

  get isValidatable() {
    return !!lodash.trim(this.ctrl?.value || '')
  }

  isLoading = false
  phoneValidationResult: { valid: boolean; format?: string } | null = null

  constructor(private store: Store<any>, private pnService: PhoneNumberService) {
    super()
  }

  ngOnInit(): void {
    this.ctrl?.valueChanges.pipe(distinctUntilChanged(), untilDestroy(this)).subscribe(() => {
      this.phoneValidationResult = null
    })
  }

  onValidate() {
    this.ctrl.markAsTouched()
    const value = this.ctrl.value
    this.isLoading = true
    this.validate(value)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          this.phoneValidationResult = data
          const errors = this.ctrl.errors
          if (data.valid) {
            this.ctrl.setErrors(isSomething(errors) ? lodash.omit(errors, 'phone') : null)
          } else {
            this.ctrl.setErrors({ ...errors, phone: true })
          }
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  setPhoneValidator() {
    this.ctrl.setAsyncValidators((ctrl: FormControl) => {
      const value = ctrl.value
      return this.validate(value).pipe(map((data) => (data?.valid ? null : { phone: true })))
    })
  }

  private validate(phoneNumber: string) {
    return this.pnService.validateV2(phoneNumber)
  }
}
